import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/shared/SEO';

export default function GovernmentLandingPage() {
  return (
    <>
      <SEO 
        title="Government Contracting Services | Omnigeist - Veteran-Owned AI & Software Modernization"
        description="Omnigeist delivers AI integration, software modernization, and custom application development for government agencies. Veteran-Owned Small Business (VOSB) specializing in .NET, Azure, and intelligent automation."
        keywords="government contracting, VOSB, veteran-owned business, government software development, AI integration government, software modernization, .NET government contracts, Azure government, Miami-Dade County"
        ogTitle="Government Contracting - Omnigeist LLC"
        ogDescription="Veteran-Owned Small Business providing AI integration, software modernization, and custom development for government agencies."
        canonical="https://omnigeist.com/gov"
      />
      <main className="min-h-screen bg-slate-950 text-white">
      {/* Hero Section */}
      <section className="relative px-6 pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="mx-auto max-w-5xl text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-5xl">Government Contracting</h1>
          <p className="mt-4 text-base text-gray-300 sm:text-lg">
            Delivering intelligent automation and AI-driven modernization for government and enterprise clients. 
            We specialize in integrating modern AI frameworks, upgrading legacy systems, and developing secure, scalable cloud solutions.
          </p>
          <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-gray-700 bg-gray-900/60 px-4 py-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            <span className="text-sm text-gray-300">Veteran-Owned Small Business (VOSB)</span>
          </div>
          <div className="mt-6 flex justify-center">
            <img
              src={encodeURI('/assets/images/sbaicons/Veteran-Owned Certified.png')}
              alt="SBA Veteran-Owned Certified"
              className="h-32 w-auto md:h-40 lg:h-48"
            />
          </div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="px-6 py-12 md:py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-3xl font-bold mb-12">Core Competencies</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">AI Integration & Automation</h3>
              <p className="text-gray-300">Intelligent agent workflows, MCP Servers, & Tool Calling Copilots.</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">Software Modernization</h3>
              <p className="text-gray-300">Upgrade legacy .NET, SQL Server, and CMS systems to secure, scalable cloud-native architectures.</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">Custom Application Development</h3>
              <p className="text-gray-300">Full lifecycle engineering for .NET 1-9, Blazor, React, and Next.js environments.</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">Training & Knowledge Transfer</h3>
              <p className="text-gray-300">Technical and end-user training to ensure sustainable adoption and self-sufficiency within client organizations.</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">AI Development Acceleration</h3>
              <p className="text-gray-300">Training teams to leverage AI tools and frameworks for fast development cycles and enhanced productivity.</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">Generative AI</h3>
              <p className="text-gray-300">Hosted or on-premises solutions for RAG, tool calling, and advanced AI capabilities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="px-6 py-12 md:py-16 bg-gray-900/20">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-3xl font-bold mb-12">Key Differentiators</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">Microsoft Ecosystem Expertise</h3>
              <p className="text-gray-300">Over 20 years with .NET, SQL Server, and Azure integration. Specialized in Microsoft's emerging AI agent frameworks and semantic AI orchestration tools.</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
              <h3 className="text-xl font-semibold mb-3">Modern Web & Cloud Stack</h3>
              <p className="text-gray-300">Proficiency across React, Node, Supabase, and Next.js for adaptive, scalable solutions. Lean & adaptive delivery with agile execution and direct senior-level engagement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Capability Statement */}
      <section className="px-6 py-12 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2">
          <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
            <h2 className="text-xl font-semibold">Certifications</h2>
            <p className="mt-2 text-gray-300">Active certifications and identifiers.</p>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-gray-300">
              <li>Veteran-Owned Small Business (VOSB)</li>
              <li>NAICS: 513210, 541511, 541512, 541519</li>
              <li>UEI: S8JVL7DJ4LK7</li>
              <li>CAGE: 148J1</li>
            </ul>
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-6">
            <h2 className="text-xl font-semibold">Capability Statement</h2>
            <p className="mt-2 text-gray-300">View our official capability statement and certification documents.</p>
            <a
              href="/assets/docs/OMNIGEIST_LLC_Capability_Statement.pdf"
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
            >
              View Capability Statement (PDF)
            </a>
            <div className="mt-4 text-sm text-gray-400">
              <p>Official docs:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li><a className="text-blue-400 hover:underline" href="/assets/docs/omnigeist-llc_ucp_approval_letter.pdf" target="_blank" rel="noreferrer">UCP Approval Letter (PDF)</a></li>
                <li><a className="text-blue-400 hover:underline" href="/assets/docs/omnigeist-llc_ucp_approval_letter_vet_cert_supp.pdf" target="_blank" rel="noreferrer">Veteran Cert Supplement (PDF)</a></li>
                <li><a className="text-blue-400 hover:underline" href="https://sam.gov/entities/view/S8JVL7DJ4LK7/coreData?status=Active&emrKeyValue=25200927~1753346919742552" target="_blank" rel="noreferrer">SAM.gov Entity Profile</a></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies / Portfolio */}
      {/* Case Studies temporarily hidden until provided */}

      {/* Contact CTA */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-4xl rounded-2xl border border-gray-800 bg-gray-900/40 p-8 text-center">
          <h2 className="text-2xl font-semibold">Get in Touch</h2>
          <p className="mt-2 text-gray-300">
            Speak with us about government contracting opportunities. Our deep technical expertise combined with 
            commitment to training and knowledge transfer enables agencies to adopt innovative technologies with confidence.
          </p>
          <div className="mt-6 mb-6">
            <p className="text-gray-300 mb-2">Point of Contact:</p>
            <p className="text-lg font-semibold">Brett Garnier</p>
            <p className="text-gray-300">Principal Architect</p>
            <div className="mt-3">
              <p className="text-gray-300">
                <a href="mailto:brett.garnier@omnigeist.com" className="text-blue-400 hover:underline">
                  brett.garnier@omnigeist.com
                </a>
              </p>
            </div>
          </div>
          <Link
            to="/contact?type=government"
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </main>
    </>
  );
}


